import {useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {BOOKING_CONSTRAINTS} from '@/constants/booking'
import {getAirports, isNearAirport} from '@/utils/airports'
import {Loader} from '@/customComponents/uiComponents/Loader.jsx'

/**
 * Location input with a SELF-RENDERED suggestion list. Mirrors the plugin's
 * customComponent/placeAutocomplete.jsx — keep the two in step.
 *
 * Google's `places.Autocomplete` widget draws its own `.pac-container` dropdown and
 * never hands us the predictions, so its order can't be changed. The client needs the
 * airport first and its terminals next (parking / cell-phone lots pushed down), so we
 * fetch predictions as DATA and render the list ourselves.
 *
 * ⚠️ PLACES API (NEW), NOT THE LEGACY ONE (2026-09-25) — same move as the plugin.
 * AutocompleteSuggestion + Place.fetchFields replaced AutocompleteService +
 * PlacesService.getDetails. The legacy API returned Ottawa's airport (YOW) as
 * `transit_station`, so isGoogleAirport missed it; the new one tags it `airport`.
 * "Places API (New)" must be enabled on the Maps key or every search comes back empty.
 *
 * BILLING:
 *  - ONE AutocompleteSessionToken is shared by every keystroke request AND the final
 *    fetchFields call, so the whole typing session bills as a single session — exactly
 *    what the widget did internally. Without this each keystroke would bill separately.
 *    The token is discarded after fetchFields; the next search starts a fresh session.
 *  - Debounced + a minimum query length, so we fire FEWER requests than the widget.
 *  - The type-restricted top-up passes (see TOP_UP_TYPES) reuse that same token, so
 *    they fall inside the one session too — they are not extra sessions.
 *
 * Google requires the "Powered by Google" mark when predictions are shown outside a
 * Google map — it is rendered at the foot of the list and must not be removed.
 */

// The debounce only fires once typing PAUSES, so these two decide how long the list
// looks dead. At 3 chars / 300ms a passenger typing a full address straight through
// saw nothing at all until they stopped — reported as "the suggestions take forever".
// Google's own widget starts at the first character and doesn't debounce; 2 chars /
// 150ms is still well short of a request per keystroke (the session token means a
// session ending in fetchFields bills once regardless).
// Nothing — Google or our airports — is searched until 3 characters are typed.
const MIN_QUERY_CHARS = 3
const DEBOUNCE_MS = 150
const MAX_SUGGESTIONS = 5

// ── Filling the list back up to MAX_SUGGESTIONS ─────────────────────────────
// Google caps EVERY autocomplete request at 5 predictions and offers no way to ask
// for more. The precision filter below then drops the city / province / street-only
// rows out of those 5 — which is why a plain (non-airport) address search often
// ended up showing just one or two rows: Google had spent 3 or 4 of its 5 slots on
// areas we can't book a pickup from.
//
// The only way to refill the list is to ask AGAIN with a `types` restriction Google
// can only answer with precise places, and merge the answers (deduped by place_id).
// Each of these is its own 5-result budget spent entirely on rows that survive the
// filter. Addresses come first — an area-heavy query is nearly always someone
// typing a street — then businesses/POIs.
//
// Fired ONLY when the unrestricted pass comes back short, so the ordinary case
// (airport, hotel, complete address) still costs exactly ONE request. All passes
// share the session token, so a session closed by fetchFields still bills as one
// session — see the BILLING note above.
// New API names: `includedPrimaryTypes` takes Table A/B types; the legacy `address`
// collection does not exist there — street_address / premise / subpremise are its members.
const TOP_UP_TYPES = [['street_address', 'premise', 'subpremise'], ['establishment']]

// The box needs room for its padding and the "powered by Google" line on top of the
// rows themselves; below this much visible space it opens UPWARDS instead.
const LIST_CHROME = 46
const MIN_SPACE_BELOW = 170
const LIST_MAX = 320   // design cap, mirrored by the SCSS fallback

// ── Suggestion ordering ─────────────────────────────────────────────────────
// Predictions are bucketed, then sorted by bucket. Within a bucket the original
// Google order is preserved (stable sort) — their relevance ranking is better than
// anything we'd invent, so we only ever move entries BETWEEN buckets.
//
// Check order matters:
//  - "Toronto Pearson Parking - Terminal 1" matches both TERMINAL and NOISE →
//    terminal wins, which is what the client asked for.
//  - "…International Airport Cell Phone Waiting Area 2" carries the word "Airport",
//    so noise is tested before the airport type or it would rank as the airport.
// "parking" is matched as a whole word, never bare "park", so real parks
// (e.g. "High Park") aren't demoted.
const TERMINAL_RE = /\bterminal\b/i
const NOISE_RE = /\bcell\s*phone\b|\bwaiting\s*area\b|\bparking\b|\bgarage\b|\bpark\s*&\s*fly\b|\bcar\s*rental\b|\bemployee\b/i

function rankBucket(prediction) {
    const name = `${prediction.structured_formatting?.main_text || ''} ${prediction.description || ''}`
    if (TERMINAL_RE.test(name)) return 1      // terminals, right under the airport
    if (NOISE_RE.test(name)) return 3         // parking / cell-phone lots / garages
    if ((prediction.types || []).includes('airport')) return 0
    return 2                                   // ordinary addresses, cities, places
}

// Nothing is dropped — a search that matches none of the patterns (a plain street
// address) keeps Google's order untouched, since every entry lands in bucket 2.
function rankPredictions(predictions) {
    return predictions
        .map((prediction, index) => ({prediction, index, bucket: rankBucket(prediction)}))
        .sort((a, b) => a.bucket - b.bucket || a.index - b.index)
        .map((entry) => entry.prediction)
}

// ── Row icons ───────────────────────────────────────────────────────────────
// A plane for an airport, a gate for one of its terminals, the map pin for everything
// else — so the three kinds of row are told apart before a word is read.
//
// Drawn INLINE rather than loaded from assets on purpose: `fill="currentColor"` is what
// lets .hopn-place-suggest-pin colour them, which is how the terminal rows tint lighter
// than the airport they sit under. An <img> would need a second asset per shade.
const PinGlyph = () => (
    <svg viewBox="0 0 15 21" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 7.12258C15 5.23355 14.2098 3.4219 12.8033 2.08616C11.3968 0.750412 9.48912 0 7.5 0C5.51088 0 3.60322 0.750412 2.1967 2.08616C0.790176 3.4219 0 5.23355 0 7.12258C0 11.0555 7.5 21 7.5 21C7.5 21 15 11.0555 15 7.12258ZM2.6087 7.12258C2.6087 6.20385 2.89557 5.30576 3.43303 4.54187C3.97049 3.77797 4.73441 3.18259 5.62818 2.83101C6.52195 2.47943 7.50542 2.38744 8.45424 2.56667C9.40306 2.74591 10.2746 3.18832 10.9587 3.83796C11.6427 4.48759 12.1086 5.31528 12.2973 6.21635C12.4861 7.11743 12.3892 8.05141 12.019 8.90021C11.6488 9.749 11.0218 10.4745 10.2175 10.9849C9.41309 11.4953 8.46741 11.7677 7.5 11.7677C6.20274 11.7677 4.95862 11.2783 4.04133 10.4072C3.12403 9.53607 2.6087 8.35455 2.6087 7.12258Z" fill="currentColor"/>
    </svg>
)

const PlaneGlyph = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
    </svg>
)

// A gate/doorway — the way into the terminal, and visually distinct from the plane at
// the small size these render at.
const TerminalGlyph = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 6v15H3v-2h2V3h9v1h5v15h2v2h-4V6h-3zm-4 5v2h2v-2h-2z"/>
    </svg>
)

function rowGlyph(prediction) {
    if (prediction.__terminal) return <TerminalGlyph/>
    if (prediction.__airport) return <PlaneGlyph/>
    return <PinGlyph/>
}

// ── OUR airports own the airport slot ───────────────────────────────────────
// Airports no longer come from Google at all. `/config/airports` is the operating
// list — the one with the terminals, catchment radii and waiting cushions the rest of
// the flow is priced from — so the passenger picks an airport from THAT, never from a
// Google row that merely looks like it. Google's airport rows are therefore dropped
// (see isGoogleAirport) and ours that match the query are listed above Google's rows.
//
// The rows are shaped like Google predictions on purpose (`place_id`, `description`,
// `structured_formatting`) so the list, the keyboard navigation and the active-row
// highlight below need no special case; only `selectSuggestion` branches, on the
// `__airport` marker.

// Stable identity for an airport across the list and the exclusion set. `id` when the
// backend sends one, else the IATA code, else the coordinate — the same fallback chain
// airportRows uses for its React keys.
function airportKey(airport) {
    return String(airport?.id ?? airport?.code ?? airport?.location?.lat ?? '')
}

// The airports already taken by the OTHER ends of this trip, as a Set of airportKey().
//
// Resolved through isNearAirport rather than by comparing addresses, which is what makes
// one rule cover every case: the other end may be the airport's own pin, one of its
// terminals, or a Google address that happens to sit inside its catchment radius — all
// three resolve to the same airport, so all three suppress it here.
function excludedAirportIds(points) {
    const ids = new Set()
    ;(Array.isArray(points) ? points : []).forEach((point) => {
        const airport = isNearAirport(point?.lat, point?.lng)
        if (airport) ids.add(airportKey(airport))
    })
    return ids
}

// The API doesn't promise a field name for a terminal's label, so fall through the
// plausible ones and finally to its address. Never blank — a nameless row is unpickable.
function terminalName(terminal, index) {
    return terminal?.name || terminal?.title || terminal?.label || terminal?.code
        || terminal?.location?.address || `Terminal ${index + 1}`
}

// One airport → its own row followed by a row per terminal, indented beneath it.
//
// Terminals are listed because they are what the passenger is actually picked up from:
// Google returns "YYZ Terminal 1" as a place kilometres from the airport pin, and the
// backend gives each terminal its own coordinate and catchment `radius_meters` for exactly
// that reason. Offering only the airport pin would send the driver to the wrong side of
// the field.
function airportRows(airport) {
    const location = airport?.location || {}
    const code = airport?.code ? ` (${airport.code})` : ''
    const key = airport?.id ?? airport?.code ?? location.lat

    const rows = [{
        place_id: `hopn-airport-${key}`,
        description: location.address || airport?.name || '',
        structured_formatting: {
            main_text: `${airport?.name || ''}${code}`.trim(),
            secondary_text: location.address || [location.city, location.state].filter(Boolean).join(', '),
        },
        __airport: airport,
    }]

    ;(Array.isArray(airport?.terminals) ? airport.terminals : []).forEach((terminal, index) => {
        const tLocation = terminal?.location || {}
        // A terminal with no coordinate can't be routed to, so it isn't offered.
        if (!Number.isFinite(Number(tLocation.lat)) || !Number.isFinite(Number(tLocation.lng))) return
        const name = terminalName(terminal, index)
        rows.push({
            place_id: `hopn-terminal-${key}-${terminal?.id ?? index}`,
            // What lands in the input, and in the booking payload's `address`: the terminal
            // alone ("Terminal 1") is meaningless once it is sitting in a pickup field.
            description: [airport?.name, name].filter(Boolean).join(' — '),
            structured_formatting: {
                main_text: name,
                secondary_text: tLocation.address || airport?.name || '',
            },
            __airport: airport,
            __terminal: terminal,
        })
    })

    return rows
}

// Which of our airports the typed text is asking for.
//
// An EMPTY query returns the whole list — that is the focus case ("show me the
// airports"), not a search. Matching is a plain case-insensitive substring over name /
// IATA code / city / its terminals' names, which gives the fall-through the client asked
// for for free: "toronto" still matches "Toronto Pearson International Airport", but
// "toronto s" matches nothing, so a passenger typing a Toronto street address drops onto
// Google after one more character rather than being stuck on the airport list.
//
// A matched airport always brings its FULL terminal list — the passenger is choosing
// WHICH terminal, so hiding the siblings would defeat the point.
//
// `excludedIds` drops an airport already picked at the OTHER end of this trip — see
// excludedAirportIds. Because the exclusion is by AIRPORT, it takes that airport's
// terminals with it: once the pickup is YYZ Terminal 1, neither YYZ nor any of its
// terminals is offered as the drop-off, which is right — that isn't a transfer.
function matchAirports(query, excludedIds) {
    const list = getAirports().filter((airport) => !excludedIds?.has(airportKey(airport)))
    const q = String(query || '').trim().toLowerCase()
    if (!q) return list
    return list.filter((airport) => {
        const terminals = (Array.isArray(airport?.terminals) ? airport.terminals : [])
            .map((terminal, index) => terminalName(terminal, index))
        const hay = [airport?.name, airport?.code, airport?.location?.city, ...terminals]
            .filter(Boolean).join(' ').toLowerCase()
        return hay.includes(q)
    })
}

// A Google prediction that IS an airport — dropped, since ours cover that slot.
//
// Terminals are deliberately kept: they are not airports, they are the pickup points
// INSIDE one, and the from-airport flow depends on them. Google tags some terminals with
// the `airport` type too, so the terminal test comes first — the same precedence, for the
// same reason, as rankBucket above.
// ⚠️ A TRANSIT STOP WHOSE LABEL SAYS "AIRPORT" COUNTS AS THE AIRPORT. Google tags the stops
// at YOW ("Airport ~ Aéroport, Ottawa International Airport (YOW)") `bus_stop` /
// `transit_station`, never `airport` — yet picking one reads to the passenger (and to the
// backend) as an airport booking. So a transit row with "airport" in its name or address
// is dropped too. Trade-off, accepted: an ordinary stop named e.g. "Airport Rd / Main St"
// is hidden as well.
const AIRPORT_WORD_RE = /\bairport\b|a[ée]roport/i
const TRANSIT_TYPES = ['transit_station', 'transit_stop', 'bus_stop', 'bus_station', 'train_station', 'subway_station', 'light_rail_station', 'tram_stop']

function isGoogleAirport(prediction) {
    const name = `${prediction.structured_formatting?.main_text || ''} ${prediction.description || ''}`
    if (TERMINAL_RE.test(name)) return false
    const types = prediction.types || []
    if (types.includes('airport')) return true
    return types.some((type) => TRANSIT_TYPES.includes(type)) && AIRPORT_WORD_RE.test(name)
}

// ── Precision filter ────────────────────────────────────────────────────────
// Google happily suggests whole cities, provinces and countries ("Oakville, ON,
// Canada") alongside real addresses. A car can't pick anyone up from an entire city,
// and those entries carry no postal_code, so selecting one only ever ended in the
// rejection message. They are dropped from the LIST instead, so the passenger never
// picks one in the first place.
//
// An area type is only disqualifying when the entry is NOT also an establishment: a
// business or POI stays even if Google tags it with an area type as well (airports,
// hotels and terminals must keep working — they are the core of this flow).
//
// `route` is in here on purpose: a street with no number ("Yonge Street") is no more
// a pickup point than a city is, and has no postal code either.
const AREA_TYPES = new Set([
    'country',
    'administrative_area_level_1',
    'administrative_area_level_2',
    'administrative_area_level_3',
    'administrative_area_level_4',
    'administrative_area_level_5',
    'colloquial_area',
    'locality',
    'sublocality',
    'sublocality_level_1',
    'neighborhood',
    'postal_code',
    'postal_code_prefix',
    'route',
])

function isPrecisePlace(prediction) {
    const types = prediction?.types || []
    if (types.includes('establishment') || types.includes('point_of_interest')) return true
    return !types.some((type) => AREA_TYPES.has(type))
}

// Pull the fields the booking flow needs out of a Place Details response.
function extractPlace(place) {
    const lat = place?.location?.lat() ?? null
    const lng = place?.location?.lng() ?? null

    let city = ''
    let state = ''
    let country = ''
    let postal_code = ''
    // New API shape — `addressComponents[].longText`.
    for (const component of place?.addressComponents || []) {
        const types = component.types || []
        if (!city && types.includes('locality')) city = component.longText
        if (!city && types.includes('sublocality_level_1')) city = component.longText
        if (!city && types.includes('administrative_area_level_2')) city = component.longText
        if (!city && types.includes('neighborhood')) city = component.longText
        if (types.includes('administrative_area_level_1')) state = component.longText
        if (types.includes('country')) country = component.longText
        if (types.includes('postal_code')) postal_code = component.longText
    }

    return {lat, lng, city, state, country, postal_code, types: place?.types || []}
}

// Rejection message shown under the input. A place we can't fully resolve is refused
// outright — we never invent the missing part. One message for every rejection reason:
// which field was missing is our problem, not something to explain to the passenger.
// The console.warn at each guard is where the actual cause is recorded.
const ERR_PLACE = "Please pick a more specific address"
// ── Service area ────────────────────────────────────────────────────────────
// /config/app → booking.service_area: {enabled, center:{lat,lng}, radius_km}. Read
// through the getter at CALL time, never at module scope: it reads the react-query
// cache, which is populated by AppConfigLoader at app root (App.jsx).
//
/**
 * ⚠️ THE COUNTRY FILTER IS OFF EVERYWHERE, INCLUDING LIVE.
 *
 * `componentRestrictions: {country: …}` is a HARD filter: Google returns nothing outside
 * the configured country. It used to be on in `build:live` only, on the reading that every
 * real trip is inside the service country — but it was switched off in live too, by
 * request, so a passenger can type an address Google knows about regardless of where it is.
 *
 * ⚠️ THE SERVICE AREA STILL APPLIES. `locationRestriction` below is a separate hard filter,
 * a radius the BACKEND configures (`SERVICE_AREA.enabled`), and it is untouched by this.
 * Turning the country filter off does not mean any address on earth is bookable — it means
 * Google is no longer the thing doing the rejecting. That is why this could be lifted
 * safely: the service area is the rule that actually matters, and it is still enforced.
 *
 * The original live-only switch is kept below rather than deleted, so restoring it is a
 * one-line change if the wider results turn out to be noise.
 *
 * ⚠️ NO LONGER MIRRORS the plugin's copy in resources/jsx/customComponent/placeAutocomplete.jsx.
 * That one keeps the live-only filter; this one is off in live as well. The divergence is
 * deliberate — do not "fix" it by matching them up again.
 */
// const RESTRICT_TO_COUNTRY = import.meta.env.MODE === 'production'
const RESTRICT_TO_COUNTRY = false

// Returns null when the config hasn't landed or the area is disabled — no restriction
// is applied in that case, which is the same behaviour as before this existed.
function serviceArea() {
    const area = BOOKING_CONSTRAINTS.SERVICE_AREA
    const lat = Number(area?.center?.lat)
    const lng = Number(area?.center?.lng)
    const km = Number(area?.radius_km)
    if (!area?.enabled || !Number.isFinite(lat) || !Number.isFinite(lng) || !(km > 0)) return null
    return {lat, lng, radiusMeters: km * 1000}
}

// Metres between two points (haversine) — same maths as hooks/useAirportDetector.
function distanceMeters(lat1, lng1, lat2, lng2) {
    const toRad = (v) => (v * Math.PI) / 180
    const R = 6371000
    const dLat = toRad(lat2 - lat1)
    const dLng = toRad(lng2 - lng1)
    const a = Math.sin(dLat / 2) ** 2
        + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

// BACKFILL ONLY — NOT for the autocomplete selection path.
// A trip loaded from GET /trips/{id} carries no postal_code on its origin/destination,
// so useEditTrip / useConfirmTrip reverse-geocode the stored coordinates before the
// route call. Those addresses were already chosen by the passenger; there is no pick to
// refuse. When the PASSENGER picks a place with no postal code we now reject it instead
// (see the guard in the fetchFields callback) — do not wire this back in there.
export function reverseGeocodePostalCode(lat, lng) {
    return new Promise((resolve) => {
        try {
            const geocoder = new window.google.maps.Geocoder()
            geocoder.geocode({location: {lat, lng}}, (results, status) => {
                if (status === 'OK' && Array.isArray(results)) {
                    for (const result of results) {
                        const pc = (result.address_components || []).find((c) => c.types.includes('postal_code'))
                        if (pc) { resolve(pc.long_name); return }
                    }
                    console.warn('[hopn] reverse-geocode returned no postal_code for', lat, lng)
                } else {
                    // A REQUEST_DENIED here almost always means the "Geocoding API" is not
                    // enabled for this Maps key (Places and Geocoding are enabled separately
                    // in Google Cloud). Enable it so city-level pickups get a postal_code.
                    console.warn('[hopn] reverse-geocode failed — status:', status, 'at', lat, lng)
                }
                resolve('')
            })
        } catch (e) {
            console.warn('[hopn] reverse-geocode threw:', e)
            resolve('')
        }
    })
}

// A new-API suggestion reshaped like a legacy prediction (`place_id`, `description`,
// `structured_formatting`, `types`), so the ranking, the filters, the airport rows and
// the list needed no change. The original rides along for `toPlace()`.
function toLegacyShape(placePrediction) {
    return {
        place_id: placePrediction.placeId,
        description: placePrediction.text?.text || '',
        structured_formatting: {
            main_text: placePrediction.mainText?.text || placePrediction.text?.text || '',
            secondary_text: placePrediction.secondaryText?.text || '',
        },
        types: placePrediction.types || [],
        distance_meters: placePrediction.distanceMeters,
        __prediction: placePrediction,
    }
}

// The service-area circle as the rectangle around it. The new API's
// `locationRestriction` takes bounds only, no circle; the corners it lets through are
// dropped by distance in fetchSuggestions (via `origin` → distanceMeters).
function circleBounds(area) {
    const dLat = area.radiusMeters / 111320
    const dLng = area.radiusMeters / (111320 * Math.cos((area.lat * Math.PI) / 180))
    return {south: area.lat - dLat, north: area.lat + dLat, west: area.lng - dLng, east: area.lng + dLng}
}

// `excludeNear` — the other ends of this trip, as [{lat, lng}]. Any airport one of them
// resolves to is kept out of this field's list, so the same airport can't be offered as
// both the pickup and the drop-off.
export default function PlaceAutocomplete({id, value, placeholder, onPlaceSelected, onFocus, onChange, readOnly, className, onError, excludeNear}) {
    const inputRef = useRef(null)
    const callbackRef = useRef(onPlaceSelected)
    const errorCallbackRef = useRef(onError)
    const excludeNearRef = useRef(excludeNear)
    const serviceRef = useRef(null)        // places.AutocompleteSuggestion (new API)
    const sessionTokenRef = useRef(null)   // one per typing session — see billing note
    const debounceRef = useRef(null)
    const requestSeqRef = useRef(0)        // drops out-of-order responses
    const pendingQueryRef = useRef(null)   // typed before the SDK finished loading
    const airportRowsRef = useRef([])      // OUR airport rows for the current query, listed above Google's

    const [suggestions, setSuggestions] = useState([])
    const [activeIndex, setActiveIndex] = useState(-1)
    const [anchor, setAnchor] = useState(null) // {top,left,width,flip,maxList}
    const [error, setError] = useState('')     // rejection message under the input
    const [loading, setLoading] = useState(false) // a lookup is in flight
    const [noResults, setNoResults] = useState(false) // search finished with nothing to list

    // Keep the callbacks — and the sibling locations the exclusion reads — current
    // without re-running the setup effect.
    useEffect(() => {
        callbackRef.current = onPlaceSelected
        errorCallbackRef.current = onError
        excludeNearRef.current = excludeNear
    })

    // A caller that passes `onError` renders the message ITSELF and the inline copy
    // below is suppressed, so it can't show twice. The banner uses this to put the
    // message under the whole search bar (outside .banner-search-area) instead of
    // under the input; every other call site omits the prop and keeps the inline one.
    useEffect(() => {
        errorCallbackRef.current?.(error)
    }, [error])

    const closeList = () => {
        setSuggestions([])
        setActiveIndex(-1)
        setLoading(false)
        setNoResults(false)
        pendingQueryRef.current = null
    }

    // The list is portalled to <body> and positioned against the input, so a modal's
    // overflow can't clip it.
    //
    // iOS keyboard, in the two coordinate systems that matter here:
    //  - `getBoundingClientRect()` reports against the LAYOUT viewport, and so does
    //    `position: fixed` — iOS keeps fixed elements pinned to the layout viewport,
    //    which is exactly why fixed footers vanish under the keyboard. The two agree,
    //    so the input's rect needs NO correction. Adding `visualViewport.offsetTop`
    //    (what this used to do) shifted the list down by however far the keyboard had
    //    scrolled the page — i.e. straight behind the keyboard, and only on iPhone,
    //    which is why Android looked right.
    //  - The VISIBLE strip is a different matter: with the keyboard up it is shorter
    //    than the layout viewport and scrolled inside it, so it ends at
    //    `offsetTop + height`, far short of window.innerHeight. That is what decides
    //    how tall the list may be and whether it still fits below the input at all.
    const measure = () => {
        const input = inputRef.current
        if (!input) return
        // Span the whole field box (the bordered `.inner` in the booking-locations form),
        // not just the bare input — in a stopover row the input sits beside a drag handle
        // and inside padding, which left the list far narrower than the field.
        const rect = (input.closest('.inner') || input).getBoundingClientRect()

        const vv = window.visualViewport
        const viewTop = vv ? vv.offsetTop : 0
        const viewBottom = vv ? vv.offsetTop + vv.height : window.innerHeight

        const spaceBelow = viewBottom - rect.bottom
        const spaceAbove = rect.top - viewTop

        /*
         * ⚠️ PHONES: NO COORDINATES AT ALL — the list is drawn INSIDE the field's own box
         * (`.banner-input-wrap` / `.add-location`, both `position: relative`), pinned to the
         * input's bottom edge, and simply runs on behind the keyboard.
         *
         * Every positioned version (`fixed` against the viewport, then document offsets)
         * went wrong on a real iPhone — the keyboard and Safari's floating bar make the
         * viewports disagree, and the list landed on top of the field — while the Mac's
         * iOS simulator looked perfect. In-flow there is nothing to measure and nothing to
         * disagree: it is under the field because it is laid out under the field.
         */
        const touch = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches
        if (touch) {
            // Still in-flow, but stretched to the whole field box (`.inner`) rather than the
            // input's own wrapper. These are offsets BETWEEN two elements of the same layout,
            // so the viewport disagreements above cancel out.
            const box = input.closest('.inner')
            const host = input.offsetParent
            // Inside a modal the list is clipped by the modal body, so cap it to the room
            // left under where it starts — the remaining suggestions scroll inside the list.
            // Measured whichever way the list is placed (with or without a `.inner` box).
            const modalBody = input.closest('.hopn-modal-body')
            const listTop = box ? box.getBoundingClientRect().bottom : (host || input).getBoundingClientRect().bottom
            const room = modalBody ? modalBody.getBoundingClientRect().bottom - listTop - 12 : LIST_MAX
            const maxList = Math.min(LIST_MAX, Math.max(room - LIST_CHROME, 40))
            if (box && host) {
                const b = box.getBoundingClientRect()
                const h = host.getBoundingClientRect()
                setAnchor({inline: true, maxList, box: {top: b.bottom - h.top, left: b.left - h.left, width: b.width}})
            } else {
                setAnchor({inline: true, maxList})
            }
            return
        }
        // Flip only when below is genuinely unusable AND above is roomier — a keyboard
        // sitting right under the field is the case this exists for.
        const flip = spaceBelow < MIN_SPACE_BELOW && spaceAbove > spaceBelow
        const room = Math.max(flip ? spaceAbove : spaceBelow, 0)

        /* The fixed containing block's own box — the viewport, or a transformed ancestor
           when one exists. A zero-sized probe reports only its corner; stretched to
           `inset:0` it reports the height too, which the flipped case below needs. */
        const probe = document.createElement('div')
        probe.style.cssText = 'position:fixed;inset:0;visibility:hidden;pointer-events:none'
        document.body.appendChild(probe)
        const block = probe.getBoundingClientRect()
        probe.remove()

        setAnchor({
            /*
             * ⚠️ A FLIPPED LIST IS ANCHORED BY ITS BOTTOM, NOT PULLED UP BY A TRANSFORM.
             *
             * It used to set `top` to the input's own top and let the CSS
             * `translateY(-100%)` lift it — which positions the box by its RENDERED height,
             * a number that does not exist until after layout. On iOS that lands wrong often
             * enough to be reported as the list sitting on top of the field: it is
             * re-measured while the keyboard is still animating, and a translate resolved
             * against the previous frame's height leaves the box short.
             *
             * `bottom` needs no height at all. The box's lower edge IS the input's upper
             * edge, whatever the box turns out to be, so it cannot overlap the field.
             */
            bottom: flip ? block.height - (rect.top - block.top) : null,
            /* ⚠️ MEASURED FROM THE FIXED BOX'S OWN ORIGIN, as the plugin does. The input's
               rect is against the viewport; the portalled list is positioned against the
               fixed containing block, and on iPhone with the keyboard up (or under a
               transformed ancestor) the two do not start at the same point. Using the raw
               rect laid the list over the field on the first step; the plugin subtracts the
               origin and never did. */
            top: (flip ? rect.top : rect.bottom) - block.top,
            left: rect.left - block.left,
            width: rect.width,
            flip,
            // Cap the rows so the box always ends inside the visible strip rather than
            // running on underneath the keyboard. Never taller than the design's 320px,
            // never so short it can't show a row (it scrolls instead).
            maxList: Math.min(LIST_MAX, Math.max(room - LIST_CHROME, 88)),
        })
    }

    // Boot the Google services once the SDK is present.
    useEffect(() => {
        let cancelled = false
        let waitTimer = null

        const waitForGoogle = () => {
            if (cancelled) return
            if (window.google?.maps?.places?.AutocompleteSuggestion) {
                serviceRef.current = window.google.maps.places.AutocompleteSuggestion
                // Anything typed while the SDK was still loading was dropped on the
                // floor — the debounce had already fired, so those characters never
                // produced a request and the list simply stayed empty until another
                // key was pressed. Replay it instead.
                const pending = pendingQueryRef.current
                if (pending) fetchSuggestions(pending)
            } else {
                waitTimer = setTimeout(waitForGoogle, 50)
            }
        }
        waitForGoogle()

        return () => {
            cancelled = true
            if (waitTimer) clearTimeout(waitTimer)
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [])

    // Keep the list pinned to the input while the page/keyboard moves. Live from the
    // "Searching…" state too — on iOS the keyboard is often still animating then, so
    // the anchor measured at dispatch is already stale by the time results land.
    useEffect(() => {
        if (!suggestions.length && !loading && !noResults) return
        const onMove = () => measure()
        window.addEventListener('resize', onMove)
        window.addEventListener('scroll', onMove, true) // capture: any scrolling ancestor
        const vv = window.visualViewport
        if (vv) {
            vv.addEventListener('resize', onMove)
            vv.addEventListener('scroll', onMove)
        }
        return () => {
            window.removeEventListener('resize', onMove)
            window.removeEventListener('scroll', onMove, true)
            if (vv) {
                vv.removeEventListener('resize', onMove)
                vv.removeEventListener('scroll', onMove)
            }
        }
    }, [suggestions.length, loading, noResults])

    // One autocomplete call. Never rejects: a failure (API not enabled, key restricted,
    // quota) is logged and comes back as an empty list, like "no matches".
    const predict = async (service, request) => {
        try {
            const {suggestions} = await service.fetchAutocompleteSuggestions(request)
            return (suggestions || [])
                .map((suggestion) => suggestion.placePrediction)
                .filter(Boolean)
                .map(toLegacyShape)
        } catch (e) {
            console.warn('[hopn] place predictions failed — is "Places API (New)" enabled on this key?',
                '| query:', request.input, e)
            return []
        }
    }

    const fetchSuggestions = async (query) => {
        const service = serviceRef.current
        if (!service) {
            // Hold the query for the boot effect above rather than losing it.
            pendingQueryRef.current = query
            measure()
            setLoading(true)
            return
        }
        pendingQueryRef.current = null

        // One token for the whole session (keystrokes + the closing fetchFields).
        if (!sessionTokenRef.current) {
            sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken()
        }

        const request = {
            input: query,
            sessionToken: sessionTokenRef.current,
        }

        // Live builds only — see RESTRICT_TO_COUNTRY. Added as a property rather than left
        // in the literal with an undefined value: Google reads the KEY, so
        // `componentRestrictions: undefined` is not reliably the same as omitting it.
        if (RESTRICT_TO_COUNTRY) {
            request.includedRegionCodes = [BOOKING_CONSTRAINTS.DEFAULT_COUNTRY]
        }

        // Service-area restriction, applied by GOOGLE rather than by us — the radius
        // counterpart of componentRestrictions above. Costs nothing extra (same
        // Autocomplete SKU, same session token).
        //
        // It MUST be `locationRestriction` (a hard filter: "results outside given
        // location will not be returned"). `location` + `radius` are only a relevance
        // BIAS, and `strictBounds` — which would harden them — is a property of the
        // Autocomplete WIDGET, not of this request, so it is silently ignored here.
        // That is why out-of-area airports were still being listed.
        const area = serviceArea()
        if (area) {
            // Bounds only in the new API — see circleBounds. `origin` makes Google return
            // each row's distanceMeters, so the rectangle's corners are dropped below.
            request.locationRestriction = circleBounds(area)
            request.origin = {lat: area.lat, lng: area.lng}
        }

        const seq = ++requestSeqRef.current
        // Anchor the box now so the "Searching…" row can show while Google answers —
        // an empty screen for the length of a mobile round-trip is what reads as slow.
        measure()
        setLoading(true)

        const predictions = await predict(service, request)
        // A slower earlier request must not overwrite a newer response.
        if (seq !== requestSeqRef.current) return

        if (!predictions.length) {
            // Nothing, or a fault (already logged in predict). No top-up pass: the
            // restricted passes are strict SUBSETS of this one, so a query with nothing
            // at all can't gain rows from them. Our airport rows (if any) stay up.
            setLoading(false)
            showRows([], true)
            return
        }

        // Filter as we go, and never list the same place twice — the top-up passes
        // deliberately overlap the primary one.
        const seen = new Set()
        const usable = []
        // Airports are ours to offer, so Google's are dropped here — but only while we
        // actually HAVE a list to offer instead. `/config/airports` is prefetched at boot;
        // if it hasn't landed (or came back empty) this filter would leave the passenger
        // unable to pick an airport at all, so it stands down in that case.
        const ownAirports = getAirports().length > 0
        const collect = (list) => {
            list.forEach((prediction) => {
                if (!isPrecisePlace(prediction) || seen.has(prediction.place_id)) return
                // The service-area rectangle's corners — see circleBounds.
                if (area && Number.isFinite(prediction.distance_meters) && prediction.distance_meters > area.radiusMeters) return
                if (ownAirports && isGoogleAirport(prediction)) return
                seen.add(prediction.place_id)
                usable.push(prediction)
            })
        }
        // Ranked over the MERGED set, so a terminal still floats up wherever it came
        // from and parking lots still sink.
        const show = (final) => showRows(rankPredictions(usable).slice(0, MAX_SUGGESTIONS), final)

        collect(predictions)
        // Paint what we already have rather than holding the list back for the top-up
        // round-trip — this pass is never slower than it was before.
        if (usable.length) show()
        if (usable.length >= MAX_SUGGESTIONS) {
            setLoading(false)
            return
        }

        // Short list — refill it. Both passes go out together, so this costs one extra
        // round-trip, not two. `loading` stays true meanwhile: with rows already on
        // screen it changes nothing visually, and with none it keeps the spinner up.
        const topUps = await Promise.all(
            TOP_UP_TYPES.map((types) => predict(service, {...request, includedPrimaryTypes: types}))
        )
        if (seq !== requestSeqRef.current) return
        setLoading(false)
        topUps.forEach((list) => collect(list))

        show(true)
    }

    // The list is OUR matching airports (each followed by its terminals) on top, then
    // Google's rows. `googleRows` is [] while Google hasn't answered or found nothing.
    // `final` marks the search as finished: with nothing to list, the box stays open on
    // "No results found" instead of silently closing.
    const showRows = (googleRows, final = false) => {
        // Our terminal row is dropped when Google lists the same terminal (by name) —
        // otherwise "YYZ Terminal 1" shows twice. Our airport rows always stay.
        const rowName = (row) => (row.structured_formatting?.main_text || '').trim().toLowerCase()
        const googleNames = new Set(googleRows.map(rowName))
        const ownRows = airportRowsRef.current.filter((row) => !(row.__terminal && googleNames.has(rowName(row))))
        const rows = [...ownRows, ...googleRows]
        if (!rows.length) {
            closeList()
            if (final) {
                measure()
                setNoResults(true)
            }
            return
        }
        measure()
        setNoResults(false)
        setSuggestions(rows)
        setActiveIndex(-1)
    }

    // No list on focus — it opens once the passenger types. The caller's own onFocus still
    // runs (several call sites use it to scroll the field into view).
    const handleFocus = (event) => {
        onFocus?.(event)
    }

    // When the query matches our airports, ONLY those are listed ("yyz" → the airport, then
    // YYZ Terminal 1, Terminal 3); otherwise Google's suggestions as normal (Google's own
    // airport rows are still dropped — see isGoogleAirport).
    const handleChange = (event) => {
        onChange?.(event)

        const query = event.target.value.trim()
        // Typing again is the retry — drop the previous rejection.
        if (error) setError('')
        if (debounceRef.current) clearTimeout(debounceRef.current)

        // The sequence bump drops a Google response still in flight for an earlier
        // keystroke, so it can't paint stale rows under the new airport rows.
        requestSeqRef.current += 1
        pendingQueryRef.current = null

        if (query.length < MIN_QUERY_CHARS) {
            airportRowsRef.current = []
            closeList()
            return
        }

        // Resolved per keystroke, not at mount: the other field may have been filled in
        // since this component last rendered.
        airportRowsRef.current = matchAirports(query, excludedAirportIds(excludeNearRef.current)).flatMap(airportRows)

        // The query names one of OUR airports → that is the whole list: the airport and its
        // terminals, and no Google rows under them. Google is only asked when nothing of
        // ours matches, so an address search ("123 King St") lists places as normal.
        if (airportRowsRef.current.length) {
            setLoading(false)
            showRows([], true)
            return
        }

        // Airport rows now; Google's follow after the debounce. With no airport rows the
        // box shows the spinner meanwhile, so the list visibly follows the keystrokes.
        showRows([])
        measure()
        setLoading(true)

        debounceRef.current = setTimeout(() => fetchSuggestions(query), DEBOUNCE_MS)
    }

    // One of OUR airports (or its terminal) was picked. No Place Details call — the record
    // already carries everything the booking payload needs except the postal code, which
    // the API doesn't store, so that one field is reverse-geocoded from the coordinate.
    //
    // `types: ['airport', …]` is not cosmetic: the flight steps key off
    // `destination.types.includes('airport')`.
    //
    // No service-area check either, deliberately — this IS the operating airport list. An
    // airport the admin configured is one we serve by definition; silently refusing it
    // because a radius in /config/app doesn't reach it would be a configuration bug
    // presenting as a dead click.
    const selectAirport = async (row) => {
        const {__airport: airport, __terminal: terminal, description} = row
        if (inputRef.current) inputRef.current.value = description
        closeList()

        // A terminal is routed to by ITS OWN coordinate — that is the whole reason the
        // backend stores one per terminal. The airport's city/state/country still apply
        // (a terminal's own record carries them only sometimes), so they fill the gaps.
        const airportLocation = airport?.location || {}
        const location = terminal ? {...airportLocation, ...(terminal.location || {})} : airportLocation
        const lat = Number(location.lat)
        const lng = Number(location.lng)
        const {city, state, country} = location

        if (!Number.isFinite(lat) || !Number.isFinite(lng) || !city || !state || !country) {
            console.warn('[hopn] airport rejected — incomplete record', {airport, terminal})
            if (inputRef.current) inputRef.current.value = ''
            setError(ERR_PLACE)
            return
        }

        setLoading(true)
        const postal_code = location.postal_code || await reverseGeocodePostalCode(lat, lng)
        setLoading(false)

        if (!postal_code) {
            console.warn('[hopn] airport rejected — no postal_code resolved', {lat, lng, name: description})
            if (inputRef.current) inputRef.current.value = ''
            setError(ERR_PLACE)
            return
        }

        setError('')
        callbackRef.current?.({
            lat,
            lng,
            address: description,
            types: ['airport', 'establishment', 'point_of_interest'],
            city,
            state,
            country,
            postal_code,
        })
    }

    const selectSuggestion = (prediction) => {
        if (prediction.__airport) {
            selectAirport(prediction)
            return
        }

        if (!prediction.__prediction) return

        if (inputRef.current) inputRef.current.value = prediction.description
        closeList()
        runDetails(prediction)
    }

    // Place details for a Google row. `toPlace()` carries the session token, so this call
    // closes the typing session and bills with it.
    const runDetails = async (prediction) => {
        // Tripwire for the failure mode that leaves NO trace: the details call never
        // settling, so the flow waits forever for a place that never arrives.
        let settled = false
        const watchdog = setTimeout(() => {
            if (!settled) {
                console.warn('[hopn] place details never came back — selection is stuck.',
                    'placeId:', prediction.place_id)
            }
        }, 8000)

        let place = null
        try {
            place = prediction.__prediction.toPlace()
            await place.fetchFields({fields: ['location', 'formattedAddress', 'types', 'addressComponents']})
        } catch (e) {
            place = null
            console.warn('[hopn] place details failed — clearing input', e)
        }
        settled = true
        clearTimeout(watchdog)
        // The session ends here whatever the outcome — a new token next time.
        sessionTokenRef.current = null

        if (!place) {
            if (inputRef.current) inputRef.current.value = ''
            setError(ERR_PLACE)
            return
        }

        const {lat, lng, city, state, country, postal_code, types} = extractPlace(place)
        // An address we can't resolve fully is rejected rather than silently
        // booked against a half-known location.
        if (!lat || !lng || !city || !state || !country) {
            console.warn('[hopn] place rejected — incomplete address', {lat, lng, city, state, country})
            if (inputRef.current) inputRef.current.value = ''
            setError(ERR_PLACE)
            return
        }

        // Service area. Out-of-area places are handled by NOT OFFERING them —
        // locationRestriction keeps them out of the list — so there is deliberately
        // no message here: the passenger is never told "we don't serve this", they
        // simply never see it. This is only the silent backstop for anything that
        // slips past it (a radius Google won't honour, or the config landing after
        // the predictions were fetched).
        const area = serviceArea()
        if (area && distanceMeters(lat, lng, area.lat, area.lng) > area.radiusMeters) {
            console.warn('[hopn] place rejected — outside service area', {lat, lng})
            if (inputRef.current) inputRef.current.value = ''
            return
        }

        // The backend requires a postal_code, and city-level places (e.g.
        // "Oakville, ON, Canada") don't carry one. We used to reverse-geocode the
        // centroid and send whatever postal code sat there — a real code, but an
        // arbitrary one the passenger never chose. Now the pick is REFUSED and the
        // passenger is asked for a more specific address. Do not reinstate a
        // fallback on THIS path (reverseGeocodePostalCode below still exists, but
        // only for backfilling trips the API returns without a postal code).
        if (!postal_code) {
            console.warn('[hopn] place rejected — no postal_code', {lat, lng, city})
            if (inputRef.current) inputRef.current.value = ''
            setError(ERR_PLACE)
            return
        }

        setError('')
        callbackRef.current?.({
            lat,
            lng,
            address: prediction.description,
            types,
            city,
            state,
            country,
            postal_code,
        })
    }

    const handleKeyDown = (event) => {
        if (!suggestions.length) return
        if (event.key === 'ArrowDown') {
            event.preventDefault()
            setActiveIndex((i) => (i + 1) % suggestions.length)
        } else if (event.key === 'ArrowUp') {
            event.preventDefault()
            setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1))
        } else if (event.key === 'Enter') {
            if (activeIndex >= 0) {
                event.preventDefault()
                selectSuggestion(suggestions[activeIndex])
            }
        } else if (event.key === 'Escape') {
            closeList()
        }
    }

    /*
     * ⚠️ THE IN-PLACE LIST HAS TO BE LIFTED OVER WHAT FOLLOWS IT.
     *
     * Drawn inside the field's box, the list can only stack as high as that box's own
     * ancestors do — so on the location step the NEXT field (drop-off under pickup) and the
     * cards under drop-off painted straight over it, leaving one visible row. While it is
     * open, every ancestor up to the banner is raised above its siblings, and restored
     * exactly as it was on close.
     */
    const listOpen = !!anchor?.inline && (suggestions.length > 0 || loading || noResults)
    useEffect(() => {
        if (!listOpen) return undefined
        const lifted = []
        /* EVERY ancestor up to the banner, not only the positioned ones: the card that
           covered the drop-off list is a sibling of a STATIC box, and only a raised box can
           out-stack it. `position: relative` with no offsets moves nothing. */
        for (let el = inputRef.current?.parentElement; el && el !== document.body; el = el.parentElement) {
            const cs = getComputedStyle(el)
            lifted.push([el, el.style.zIndex, el.style.position])
            el.style.zIndex = '1000'
            if (cs.position === 'static') el.style.position = 'relative'
            if (el.classList.contains('hopn-banner-area') || el.classList.contains('hopn-book-ride')) break
        }
        return () => {
            for (const [el, zIndex, position] of lifted) {
                el.style.zIndex = zIndex
                el.style.position = position
            }
        }
    }, [listOpen])

    /* Portalled to <body> on desktop (so a modal's overflow cannot clip it); rendered in
       place on phones, where it sits in the field's own box — see `measure`. */
    const portalOrInline = (node, target) => (anchor?.inline ? node : createPortal(node, target))

    return (
        <>
            <input
                id={id}
                ref={inputRef}
                defaultValue={value || ''}
                type="text"
                placeholder={placeholder || 'Enter a location'}
                readOnly={readOnly}
                autoComplete="off"
                onFocus={handleFocus}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                // Deferred so a click on a suggestion (which blurs the input) still
                // lands — the option's onMouseDown fires first.
                onBlur={() => setTimeout(closeList, 150)}
                className={className ?? 'w-full border-0! outline-none text-base bg-transparent p-1 mr-5 mb-0! placeholder:text-(--text-primary)'}
            />

            {error && !onError && <div className="hopn-place-error">{error}</div>}

            {(suggestions.length > 0 || loading || noResults) && anchor && portalOrInline(
                <div
                    /* ⚠️ NO `is-flipped` CLASS. A flipped list is bottom-anchored (see
                       `measure`), so the CSS `translateY(-100%)` that used to lift it would
                       now pull it a second time — clean off the top of the screen. */
                    className="hopn-place-suggest"
                    style={{
                        // Phones: in the field's own box, under the input — see `measure`.
                        ...(anchor.inline
                            ? (anchor.box
                                ? {position: 'absolute', top: `${anchor.box.top}px`, left: `${anchor.box.left}px`, width: `${anchor.box.width}px`}
                                : {position: 'absolute', top: '100%', left: 0, right: 0})
                            : {
                                ...(anchor.bottom === null
                                    ? {top: `${anchor.top}px`}
                                    : {bottom: `${anchor.bottom}px`}),
                                left: `${anchor.left}px`,
                                width: `${anchor.width}px`,
                            }),
                        // Rows only — the SCSS keeps this under the 320px design cap.
                        '--hopn-suggest-max': `${anchor.maxList}px`,
                    }}
                    role="listbox"
                >
                    {suggestions.length === 0 && !noResults && (
                        <div className="hopn-place-suggest-loading">
                            <Loader/>
                        </div>
                    )}
                    {noResults && (
                        <div className="hopn-place-suggest-empty">No results found</div>
                    )}
                    <ul>
                        {suggestions.map((prediction, index) => (
                            <li
                                key={prediction.place_id}
                                role="option"
                                aria-selected={index === activeIndex}
                                className={[
                                    index === activeIndex ? 'active' : '',
                                    prediction.__terminal ? 'is-terminal' : (prediction.__airport ? 'is-airport' : ''),
                                ].filter(Boolean).join(' ')}
                                // mousedown, not click: it fires before the input's blur.
                                onMouseDown={(e) => { e.preventDefault(); selectSuggestion(prediction) }}
                                onMouseEnter={() => setActiveIndex(index)}
                            >
                                <span className="hopn-place-suggest-pin" aria-hidden="true">
                                    {rowGlyph(prediction)}
                                </span>
                                <span className="hopn-place-suggest-text">
                                    <strong>{prediction.structured_formatting?.main_text || prediction.description}</strong>
                                    {prediction.structured_formatting?.secondary_text && (
                                        <small>{prediction.structured_formatting.secondary_text}</small>
                                    )}
                                </span>
                            </li>
                        ))}
                    </ul>
                    {/* Required by the Google Maps Platform terms whenever GOOGLE predictions
                        are displayed outside a Google map. Do not remove — but the airport
                        list is our own data, so a box showing only those carries no Google
                        content to attribute. */}
                    {suggestions.some((s) => !s.__airport) && (
                        <div className="hopn-place-suggest-credit">powered by Google</div>
                    )}
                </div>,
                document.body
            )}
        </>
    )
}
